import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateSpoolInput,
  UpdateSpoolInput,
  MoveSpoolInput,
} from '@maker-toolchest/shared';
import { spoolApi, type SpoolQueryParams } from './api';

const SPOOLS_QUERY_KEY = 'spools';

/**
 * List spools with filters
 */
export function useSpools(params?: SpoolQueryParams) {
  return useQuery({
    queryKey: [SPOOLS_QUERY_KEY, params],
    queryFn: () => spoolApi.list(params),
  });
}

/**
 * Get a single spool
 */
export function useSpool(id: string) {
  return useQuery({
    queryKey: [SPOOLS_QUERY_KEY, id],
    queryFn: () => spoolApi.get(id),
    enabled: !!id,
  });
}

/**
 * Create a new spool
 */
export function useCreateSpool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSpoolInput) => spoolApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SPOOLS_QUERY_KEY] });
      // Also invalidate tenant filaments to update spool counts
      queryClient.invalidateQueries({ queryKey: ['tenant-filaments'] });
    },
  });
}

/**
 * Update a spool
 */
export function useUpdateSpool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSpoolInput }) =>
      spoolApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SPOOLS_QUERY_KEY] });
    },
  });
}

/**
 * Delete a spool
 */
export function useDeleteSpool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => spoolApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SPOOLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['tenant-filaments'] });
    },
  });
}

/**
 * Move a spool to a new location
 */
export function useMoveSpool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MoveSpoolInput }) =>
      spoolApi.move(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SPOOLS_QUERY_KEY] });
    },
  });
}

/**
 * Update spool weight
 */
export function useUpdateSpoolWeight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, remainingWeight }: { id: string; remainingWeight: number }) =>
      spoolApi.updateWeight(id, remainingWeight),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SPOOLS_QUERY_KEY] });
    },
  });
}
